import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getDynamicSystemInstruction } from '../constants';
import { showToast } from '../utils/helpers';
import { logConversation } from '../services/negotiationService';
import { API_CONFIG } from '../utils/apiConfig';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

interface MeoChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPriceAgreed?: (price: number) => void;
  sendTextMessage?: (text: string) => Promise<void> | void;
  agentMessages?: string[];
}

const getPricingInfo = (product: any) => {
  if (!product) return { pricing: 'Information not available' };
  const basePrice = product.per_unit_cost || 0;
  const isNegotiable = !!(product.is_negotiable && product.starting_price && product.ending_price);
  return {
    base_price: `₦${parseFloat(basePrice).toLocaleString()}`,
    currency: product.currency || 'NGN',
    negotiable: isNegotiable,
    ...(isNegotiable ? {
      min_acceptable: Number(product.starting_price),
      max_acceptable: Number(product.ending_price),
    } : {}),
    notes: isNegotiable ? 'Price is negotiable' : 'Fixed price',
  };
};

const getStockInfo = (product: any) => {
  if (!product) return { status: 'Product information not available' };
  const availableStock = (product.inventory || 0) - (product.total_sold || 0);
  return {
    status: availableStock > 0 ? 'In Stock' : 'Out of Stock',
    available_quantity: availableStock,
  };
};

const MeoChatModal: React.FC<MeoChatModalProps> = ({ isOpen, onClose, onPriceAgreed, sendTextMessage, agentMessages = [] }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const agentMsgCountRef = useRef(0);
  const idCounterRef = useRef(0);

  const historyRef = useRef<any[]>([]);
  const isLiveSession = typeof sendTextMessage === 'function';

  // Initialize with store-aware greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const storeData = (window as any).__COMPANY_DATA__;
      const productData = (window as any).__PRODUCT_DATA__;
      const storeName = storeData?.company_name || 'the store';
      const productName = productData?.product_name;

      const greeting = productName
        ? `Hi there! Welcome to ${storeName}. I'm Meo, your personal assistant for ${productName}. How can I help you today?`
        : `Hi there! Welcome to ${storeName}. I'm Meo, your store assistant. Are you looking for something specific today?`;

      setMessages([{
        id: 'welcome',
        role: 'assistant',
        text: greeting,
      }]);
      setTimeout(() => inputRef.current?.focus(), 400);
    }
  }, [isOpen, messages.length]);

  // Append new agent messages from live session
  useEffect(() => {
    if (agentMessages.length > agentMsgCountRef.current) {
      const newTexts = agentMessages.slice(agentMsgCountRef.current);
      agentMsgCountRef.current = agentMessages.length;
      for (const text of newTexts) {
        idCounterRef.current += 1;
        setMessages(prev => [...prev, { id: `agent-${idCounterRef.current}`, role: 'assistant', text }]);
      }
    }
  }, [agentMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const callGemini = async (contents: any[]): Promise<any> => {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) throw new Error('API key not found');

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system_instruction: { parts: [{ text: getDynamicSystemInstruction() }] },
          contents,
          tools: [{
            functionDeclarations: [
              {
                name: 'updatePrice',
                description: 'Update the product price after a successful negotiation.',
                parameters: {
                  type: 'OBJECT',
                  properties: {
                    newPrice: { type: 'NUMBER', description: 'The new agreed price' },
                  },
                  required: ['newPrice'],
                },
              },
              {
                name: 'checkStock',
                description: 'Check inventory, pricing (including negotiable range), product details, or delivery options.',
                parameters: {
                  type: 'OBJECT',
                  properties: {
                    inquiry_type: {
                      type: 'STRING',
                      description: 'What to check: stock_availability, pricing (includes negotiable range), product_details, delivery_options, or all',
                    },
                    variant: { type: 'STRING', description: 'Specific variant to check' },
                  },
                  required: ['inquiry_type'],
                },
              },
              {
                name: 'contactSeller',
                description: 'LAST RESORT ONLY — Alert the seller after you have attempted negotiation yourself and reached an impasse, or when the customer explicitly asks for human assistance. Do NOT call this when the customer simply asks about price or negotiation.',
                parameters: {
                  type: 'OBJECT',
                  properties: {
                    reason: { type: 'STRING', description: 'Reason for contacting seller' },
                  },
                  required: ['reason'],
                },
              },
              {
                name: 'listProducts',
                description: 'Search or list the products available in the current store. Use this when a customer asks about other products, wants to browse the catalog, or is looking for something specific.',
                parameters: {
                  type: 'OBJECT',
                  properties: {
                    search: { type: 'STRING', description: 'Optional search term to filter products by name, brand, or category' },
                    maxResults: { type: 'NUMBER', description: 'Maximum number of products to return (default 10, max 20)' },
                  },
                },
              },
            ],
          }],
        }),
      }
    );

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Gemini API error: ${response.status} ${errText}`);
    }

    return await response.json();
  };

  const executeFunction = useCallback(async (fc: any): Promise<any> => {
    const productData = (window as any).__PRODUCT_DATA__;

    switch (fc.name) {
      case 'updatePrice': {
        const newPrice = Number(fc.args?.newPrice);
        if (!newPrice || newPrice <= 0) {
          return { status: 'error', message: 'Invalid price.' };
        }
        onPriceAgreed?.(newPrice);
        showToast(`Price agreed at ₦${newPrice.toLocaleString()}`, 'success');
        return { status: 'success', newPrice, message: `Price has been updated to ₦${newPrice.toLocaleString()}` };
      }
      case 'checkStock': {
        const inquiryType = fc.args?.inquiry_type || 'stock_availability';
        switch (inquiryType) {
          case 'pricing': return getPricingInfo(productData);
          case 'product_details': return { name: productData?.product_name, description: productData?.description };
          case 'delivery_options': return { delivery: 'Available' };
          default: return getStockInfo(productData);
        }
      }
      case 'contactSeller': {
        showToast(`Seller notified: ${fc.args?.reason || 'Customer requested assistance'}`, 'info');
        return { status: 'notified', message: 'The seller has been notified.' };
      }
      case 'listProducts': {
        const search = fc.args?.search || '';
        const storeData = (window as any).__COMPANY_DATA__;
        const companyId = storeData?.username || storeData?.company_id;
        if (!companyId) return { error: 'Store information not available' };

        // Try cached products first, then fetch from API
        let products = (window as any).__STORE_PRODUCTS__;
        if (!products) {
          try {
            const response = await fetch(
              `${API_CONFIG.BUSINESS_BACKEND_URL}${API_CONFIG.STORE.STORE_V3}/${companyId}?page=1&limit=200`
            );
            const data = await response.json();
            products = data.products || data.data || [];
            (window as any).__STORE_PRODUCTS__ = products;
          } catch (err) {
            console.error('Failed to fetch store products:', err);
            return { error: 'Unable to fetch product catalog at this time' };
          }
        }

        const mapped = products.map((p: any) => ({
          name: p.product_name,
          price: p.per_unit_cost,
          currency: p.currency || 'NGN',
          inStock: (p.inventory || 0) - (p.total_sold || 0) > 0,
          negotiable: !!(p.is_negotiable && p.starting_price && p.ending_price),
          slug: p.slug || p.id,
          brand: p.brand || '',
          category: p.category || '',
        }));

        const filtered = search
          ? mapped.filter((p: any) =>
              p.name.toLowerCase().includes(search.toLowerCase()) ||
              p.brand.toLowerCase().includes(search.toLowerCase()) ||
              p.category.toLowerCase().includes(search.toLowerCase())
            )
          : mapped;

        return { products: filtered, total: filtered.length };
      }
      default:
        return { status: 'error', message: `Unknown function: ${fc.name}` };
    }
  }, [onPriceAgreed]);

  const getNegotiationSessionId = useCallback(() => {
    const ctx = (window as any).__NEGOTIATION_CONTEXT__;
    return ctx?.session_id || null;
  }, []);

  const logChatAction = useCallback(async (action: string, message: string, new_amount?: number) => {
    const sessionId = getNegotiationSessionId();
    if (!sessionId) return;
    try {
      await logConversation(sessionId, {
        action,
        message,
        actor: action === 'buyer_message' ? 'buyer' : 'seller',
        new_amount,
      });
    } catch (e) {
      // silent fail for logging
    }
  }, [getNegotiationSessionId]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput('');

    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: 'user', text };
    setMessages(prev => [...prev, userMsg]);

    // Log user message to negotiation session
    logChatAction('buyer_message', text);

    // Live session mode — send text through the shared voice session
    if (isLiveSession) {
      sendTextMessage!(text);
      return;
    }

    // Fallback mode — use generateContent API
    setIsLoading(true);
    try {
      let contents = [
        ...historyRef.current,
        { role: 'user', parts: [{ text }] },
      ];

      const MAX_TURNS = 5;
      for (let turn = 0; turn < MAX_TURNS; turn++) {
        const data = await callGemini(contents);
        const candidate = data?.candidates?.[0];
        const content = candidate?.content;

        if (!content?.parts?.length) {
          setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', text: 'Sorry, I couldn\'t process that.' }]);
          break;
        }

        const functionCallPart = content.parts.find((p: any) => p.functionCall);
        if (functionCallPart?.functionCall) {
          const fc = functionCallPart.functionCall;
          const resultData = await executeFunction(fc);

          // Log tool call actions
          if (fc.name === 'updatePrice') {
            logChatAction('price_proposed', `AI proposed price ₦${Number(fc.args?.newPrice).toLocaleString()}`, Number(fc.args?.newPrice));
          } else if (fc.name === 'contactSeller') {
            logChatAction('system_note', `AI escalated to seller: ${fc.args?.reason || ''}`);
          }

          contents.push(content);
          contents.push({
            role: 'function',
            parts: [{
              functionResponse: {
                name: fc.name,
                response: { result: resultData },
              },
            }],
          });
          continue;
        }

        const responseText = content.parts.map((p: any) => p.text || '').join('').trim();
        if (responseText) {
          setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', text: responseText }]);
          logChatAction('seller_message', responseText);
        }
        historyRef.current = [
          ...historyRef.current,
          { role: 'user', parts: [{ text }] },
          content,
        ];
        break;
      }
    } catch (err: any) {
      console.error('Chat error:', err);
      showToast('Chat error — please try again', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, isLiveSession, sendTextMessage, executeFunction, logChatAction]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white dark:bg-gray-900 w-full sm:max-w-lg sm:rounded-2xl sm:mb-0 sm:mx-4 shadow-2xl flex flex-col h-[85dvh] sm:h-[600px] animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">M</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Meo</h3>
              <p className="text-[11px] text-gray-500">Sales Assistant</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close chat"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white rounded-br-md'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-md'
                }`}
              >
                {msg.text}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              aria-label="Send message"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeoChatModal;
